import React from 'react';
import { Button, Box, Typography } from '@mui/material';

interface GoogleSignInButtonProps {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  text?: 'Sign in with Google' | 'Sign up with Google' | 'Continue with Google';
  fullWidth?: boolean;
  variant?: 'light' | 'dark';
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onClick,
  disabled = false,
  loading = false,
  text = 'Sign in with Google',
  fullWidth = true,
  variant = 'light'
}) => {
  const GoogleIcon = () => (
    <>
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        style={{ marginRight: '8px' }}
        aria-hidden="true"
      >
        <path
          fill="#4285F4"
          d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z"
        />
        <path
          fill="#EA4335"
          d="M.96 4.96C.35 6.15 0 7.55 0 9s.35 2.85.96 4.04l2.91-2.26C3.35 9.85 3.35 8.15 3.87 7.22L.96 4.96z"
        />
        <path
          fill="#FBBC05"
          d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.86.9-3.05.9-2.38 0-4.4-1.57-5.12-3.74L.96 13.04C2.44 15.98 5.48 18 9 18z"
        />
        <path
          fill="#34A853"
          d="M15.04 15.82c1.15-1.33 1.96-3.02 1.96-6.82 0-.65-.06-1.28-.17-1.9H9v3.6h3.39c-.15.83-.64 1.53-1.36 2.01l2.91 2.26c-.9.75-2.05 1.35-3.39 1.35.81-.27 1.49-.82 1.49-1.5z"
        />
      </svg>
      <span className="sr-only">Google icon</span>
    </>
  );

  const buttonStyles = {
    backgroundColor: variant === 'dark' ? '#1f1f1f' : '#ffffff',
    color: variant === 'dark' ? '#ffffff' : '#1f1f1f',
    border: variant === 'dark' ? '1px solid #454545' : '1px solid #dadce0',
    borderRadius: '4px',
    textTransform: 'none' as const,
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    height: '40px',
    boxShadow: '0 1px 2px 0 rgba(60,64,67,.30), 0 1px 3px 1px rgba(60,64,67,.15)',
    '&:hover': {
      backgroundColor: variant === 'dark' ? '#2d2d2d' : '#f8f9fa',
      boxShadow: '0 1px 3px 0 rgba(60,64,67,.30), 0 4px 8px 3px rgba(60,64,67,.15)',
    },
    '&:active': {
      backgroundColor: variant === 'dark' ? '#454545' : '#f1f3f4',
    },
    '&:focus': {
      outline: 'none',
      boxShadow: '0 1px 2px 0 rgba(60,64,67,.30), 0 1px 3px 1px rgba(60,64,67,.15), 0 0 0 2px rgba(26, 115, 232, 0.15)',
    },
    '&.Mui-disabled': {
      backgroundColor: variant === 'dark' ? '#1f1f1f' : '#ffffff',
      color: variant === 'dark' ? '#757575' : '#9aa0a6',
      border: variant === 'dark' ? '1px solid #454545' : '1px solid #dadce0',
    },
  };

  return (
    <Button
      onClick={onClick}
      disabled={disabled || loading}
      fullWidth={fullWidth}
      sx={buttonStyles}
      startIcon={loading ? null : <GoogleIcon />}
      aria-label={text}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading ? (
          <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              aria-hidden="true"
              sx={{
                width: '18px',
                height: '18px',
                marginRight: '8px',
                border: '2px solid',
                borderColor: variant === 'dark' ? '#757575' : '#9aa0a6',
                borderTopColor: variant === 'dark' ? '#ffffff' : '#1f1f1f',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
            <Typography component="span" sx={{ fontSize: '14px', fontWeight: 500 }}>
              {text === 'Sign in with Google' ? 'Signing in...' :
               text === 'Sign up with Google' ? 'Signing up...' :
               'Connecting...'}
            </Typography>
          </Box>
        ) : (
          <Typography component="span" sx={{ fontSize: '14px', fontWeight: 500 }}>
            {text}
          </Typography>
        )}
      </Box>
    </Button>
  );
};

export default GoogleSignInButton;
